import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Turnstile } from './Turnstile';
import { createNote, NotesApiError, type PublicNote } from '../api/notes';

const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';
const NICKNAME_MAX = 40;
const BODY_MAX = 1000;
const CLIENT_THROTTLE_MS = 30_000;

interface NotePostFormProps {
  landmarkId: string;
  /** Called after a POST succeeds (or fails — for rollback). */
  onSubmitStart: (tempNote: PublicNote) => string;
  onSubmitSuccess: (tempId: string, real: PublicNote) => void;
  onSubmitError: (tempId: string) => void;
}

export function NotePostForm({
  landmarkId,
  onSubmitStart,
  onSubmitSuccess,
  onSubmitError,
}: NotePostFormProps) {
  const [nickname, setNickname] = useState('');
  const [body, setBody] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const lastSubmitRef = useRef(0);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? TURNSTILE_TEST_SITE_KEY;

  const trimmedNickname = nickname.trim();
  const trimmedBody = body.trim();
  const canSubmit =
    trimmedNickname.length > 0 &&
    trimmedBody.length > 0 &&
    turnstileToken !== null &&
    !submitting;

  const onToken = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || turnstileToken === null) return;

    const elapsed = Date.now() - lastSubmitRef.current;
    if (lastSubmitRef.current > 0 && elapsed < CLIENT_THROTTLE_MS) {
      const wait = Math.ceil((CLIENT_THROTTLE_MS - elapsed) / 1000);
      setError(`Please wait ${wait}s before posting another note.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    const optimistic: PublicNote = {
      id: `pending-${crypto.randomUUID()}`,
      landmarkId,
      nickname: trimmedNickname,
      body: trimmedBody,
      createdAt: Math.floor(Date.now() / 1000),
    };
    const tempId = onSubmitStart(optimistic);

    try {
      const real = await createNote({
        landmarkId,
        nickname: trimmedNickname,
        body: trimmedBody,
        turnstileToken,
      });
      onSubmitSuccess(tempId, real);
      setNickname('');
      setBody('');
      setTurnstileToken(null);
      setTurnstileResetKey((n) => n + 1);
      lastSubmitRef.current = Date.now();
    } catch (err) {
      onSubmitError(tempId);
      const message =
        err instanceof NotesApiError
          ? err.status === 429
            ? 'You’re posting too fast — please wait a minute and try again.'
            : err.status === 403
            ? 'Captcha verification failed. Please try the captcha again.'
            : `Failed to post: ${err.message}`
          : 'Network error — please try again.';
      setError(message);
      setTurnstileToken(null);
      setTurnstileResetKey((n) => n + 1);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="note-post-form"
      style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      <h3
        style={{
          marginBottom: 'var(--space-3)',
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--text-headline)',
        }}
      >
        Add a note
      </h3>

      <label style={labelStyle} htmlFor="note-nickname">
        Your name
      </label>
      <input
        id="note-nickname"
        data-testid="note-nickname-input"
        type="text"
        value={nickname}
        onChange={(e) => setNickname(e.target.value)}
        maxLength={NICKNAME_MAX}
        placeholder="e.g. Anouk"
        required
        style={inputStyle}
        aria-label="Your name (1–40 characters)"
      />

      <label style={labelStyle} htmlFor="note-body">
        Your note
      </label>
      <textarea
        id="note-body"
        data-testid="note-body-input"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={BODY_MAX}
        rows={4}
        placeholder="What did you notice or want to share?"
        required
        style={{ ...inputStyle, fontFamily: 'var(--font-serif)', resize: 'vertical' }}
        aria-label="Your note (1–1000 characters)"
      />
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--space-1)' }}>
        {trimmedBody.length}/{BODY_MAX} characters
      </p>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Turnstile key={turnstileResetKey} siteKey={siteKey} onToken={onToken} />
      </div>

      {error !== null ? (
        <p
          data-testid="note-form-error"
          role="alert"
          style={{ marginTop: 'var(--space-2)', color: 'var(--error)', fontSize: '0.875rem' }}
        >
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="note-submit"
        aria-label="Post your note"
        style={{
          marginTop: 'var(--space-3)',
          width: '100%',
          padding: '10px var(--space-4)',
          background: canSubmit ? 'var(--primary)' : 'var(--surface-hover)',
          color: canSubmit ? '#fffdf2' : 'var(--text-muted)',
          borderRadius: 'var(--radius-sm)',
          fontWeight: 600,
          fontSize: '0.9rem',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          transition: 'background-color 120ms ease',
        }}
      >
        {submitting ? 'Posting…' : 'Post note'}
      </button>
    </form>
  );
}

const labelStyle = {
  display: 'block',
  marginTop: 'var(--space-2)',
  marginBottom: 'var(--space-1)',
  color: 'var(--text-muted)',
  fontSize: '0.8rem',
  fontWeight: 500,
} as const;

const inputStyle = {
  display: 'block',
  width: '100%',
  padding: '8px var(--space-2)',
  background: 'var(--surface-raised)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text)',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  lineHeight: 1.5,
} as const;
